/**
 * OrganizationSelector Component
 * Modern, searchable organization selector with redesigned UI
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useOrganization, Organization } from '@/contexts/OrganizationContext'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Building2,
  ChevronDown,
  Search,
  Check,
  Loader2,
  AlertCircle,
  Users,
  X,
} from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

/**
 * Generate initials from organization name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/**
 * Generate a consistent color based on organization id
 */
function getAvatarColor(id: string): string {
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-brand-secondary',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-brand-primary',
    'bg-teal-500',
    'bg-orange-500',
    'bg-pink-500',
  ]
  
  // Generate a consistent index based on id
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Organization Avatar Component
 */
function OrganizationAvatar({
  organization,
  size = 'md',
}: {
  organization: Organization
  size?: 'sm' | 'md' | 'lg'
}) {
  const initials = getInitials(organization.displayName)
  const colorClass = getAvatarColor(organization.id)

  const sizeClasses = {
    sm: 'h-7 w-7 text-[10px]',
    md: 'h-9 w-9 text-xs',
    lg: 'h-11 w-11 text-sm',
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-lg font-semibold text-white flex-shrink-0',
        colorClass,
        sizeClasses[size]
      )}
    >
      {initials}
    </div>
  )
}

/**
 * Organization Item in the list
 */
function OrganizationItem({
  organization,
  isSelected,
  onSelect,
}: {
  organization: Organization
  isSelected: boolean
  onSelect: () => void
}) {
  const { t } = useLocalization()
  
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-start transition-colors',
        'hover:bg-muted focus:bg-muted focus:outline-none',
        isSelected && 'bg-primary/5 ring-1 ring-primary/20'
      )}
    >
      <OrganizationAvatar organization={organization} size="md" />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'font-medium truncate text-sm',
              isSelected && 'text-primary'
            )}
          >
            {organization.displayName}
          </span>
        </div>
        
        <div className="flex items-center gap-3 mt-0.5">
          {organization.activeProvidersCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Users className="h-3 w-3" />
              {organization.activeProvidersCount}
            </span>
          )}
        </div>
      </div>

      {isSelected && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </button>
  )
}

/**
 * Empty State Component
 */
function EmptyState({ searchQuery }: { searchQuery: string }) {
  const { t } = useLocalization()
  
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-3">
        <Building2 className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground">{t('organization.noOrganizationsFound')}</p>
      <p className="text-xs text-muted-foreground text-center mt-1">
        {searchQuery
          ? t('organization.noResultsFor', { query: searchQuery })
          : t('organization.noOrganizationsAvailable')}
      </p>
    </div>
  )
}

/**
 * Loading State Component
 */
function LoadingState() {
  return (
    <div className="space-y-2 p-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Main OrganizationSelector Component
 */
export function OrganizationSelector() {
  const {
    currentOrganization,
    organizations,
    setCurrentOrganization,
    isLoading,
    isError,
  } = useOrganization()

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { t } = useLocalization()

  // Filter organizations based on search query
  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return organizations
    
    const query = searchQuery.toLowerCase().trim()
    return organizations.filter(
      (org) =>
        org.displayName.toLowerCase().includes(query) ||
        org.name.toLowerCase().includes(query) ||
        (org.legalName && org.legalName.toLowerCase().includes(query)) ||
        (org.description && org.description.toLowerCase().includes(query))
    )
  }, [organizations, searchQuery])

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure popover is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    } else {
      // Clear search when closing
      setSearchQuery('')
    }
  }, [isOpen])

  const handleSelect = useCallback(
    (org: Organization) => {
      setCurrentOrganization(org)
      setIsOpen(false)
      setSearchQuery('')
    },
    [setCurrentOrganization]
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    searchInputRef.current?.focus()
  }, [])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 min-w-0 sm:min-w-[240px]">
        <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
        <div className="space-y-1.5 hidden sm:block">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center gap-2 px-2 sm:px-3 py-2 min-w-0 sm:min-w-[240px] text-destructive">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <div className="hidden sm:block">
          <p className="text-sm font-medium">{t('organization.failedToLoad')}</p>
          <p className="text-xs opacity-80">{t('organization.organizationsUnavailable')}</p>
        </div>
      </div>
    )
  }

  // No organizations
  if (organizations.length === 0) {
    return (
      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 min-w-0 sm:min-w-[240px] text-muted-foreground">
        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <Building2 className="h-4 w-4" />
        </div>
        <div className="hidden sm:block">
          <p className="text-sm font-medium">{t('organization.noOrganizations')}</p>
          <p className="text-xs">{t('organization.contactSupport')}</p>
        </div>
      </div>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={isOpen}
          aria-label={t('organization.selectOrganization')}
          className={cn(
            'min-w-0 sm:min-w-[200px] md:min-w-[240px] max-w-[320px] justify-between px-2 sm:px-3 py-2 h-auto',
            'hover:bg-muted/50 transition-colors',
            isOpen && 'bg-muted/50'
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {currentOrganization && (
              <OrganizationAvatar
                organization={currentOrganization}
                size="sm"
              />
            )}
            <div className="text-start hidden sm:block min-w-0">
              <p className="text-sm font-medium truncate max-w-[120px] md:max-w-[180px]">
                {currentOrganization?.displayName || t('organization.selectOrganization')}
              </p>
              {currentOrganization && currentOrganization.activeProvidersCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {currentOrganization.activeProvidersCount}{' '}
                  {currentOrganization.activeProvidersCount === 1
                    ? t('organization.activeProvider')
                    : t('organization.activeProviders')}
                </p>
              )}
            </div>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform ms-1 sm:ms-2 flex-shrink-0',
              isOpen && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[340px] p-0 overflow-hidden"
        align="start"
        sideOffset={8}
      >
        {/* Sticky Header with search - always visible */}
        <div className="sticky top-0 z-10 p-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('organization.searchPlaceholder')}
              className="ps-9 pe-8 h-9 bg-background border-muted-foreground/20"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-muted transition-colors"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          {organizations.length > 0 && (
            <div className="flex items-center justify-between mt-2 px-1">
              <p className="text-xs text-muted-foreground">
                {searchQuery
                  ? `${filteredOrganizations.length} ${t('organization.ofOrganizations', { total: organizations.length })}`
                  : `${organizations.length} ${organizations.length === 1 ? t('organization.organization') : t('organization.organizations')}`}
              </p>
              {searchQuery && filteredOrganizations.length > 0 && (
                <Badge variant="secondary" className="text-[10px] h-5">
                  {t('common.filtered')}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Organization list */}
        <div className="max-h-[280px] overflow-y-auto overscroll-contain">
          <div className="p-2">
            {isLoading ? (
              <LoadingState />
            ) : filteredOrganizations.length === 0 ? (
              <EmptyState searchQuery={searchQuery} />
            ) : (
              <div className="space-y-1">
                {filteredOrganizations.map((org) => (
                  <OrganizationItem
                    key={org.id}
                    organization={org}
                    isSelected={currentOrganization?.id === org.id}
                    onSelect={() => handleSelect(org)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky Footer with hint */}
        {filteredOrganizations.length > 0 && (
          <div className="sticky bottom-0 px-3 py-2 border-t bg-muted/50 backdrop-blur">
            <p className="text-[10px] text-muted-foreground text-center">
              {t('organization.clickToSelect')}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
